import { Trans, useTranslation } from 'react-i18next';
import Text from '@ingka/text';
import { translateWithFallback } from 'utils/translations';
import ListView, { ListViewItem } from '@ingka/list-view';
import { UserProfileDrawer } from '@ingka-group-digital/cwds-react-user-profile';
import useAuth from 'hooks/useAuth';
import { env } from 'utils/env';
import { getCWDSUserProfile } from 'components/NavigationBar/NavigationBar.utils';
import { useNestedTranslation } from 'hooks/useNestedTranslation';
import { useToast } from 'hooks/useToast';
import LanguageSelector from 'services/Languages';
import Hyperlink from '@ingka/hyperlink';
import classes from './UserInfoPanel.module.scss';

const Details = ({ title, data }: { title: string; data: string | JSX.Element }) => (
    <div data-testid="user-info-detail">
        <Text textStyle="Heading.XS">{title}</Text>
        <Text textStyle="Body.M" className={classes['user-info-panel-detail-data']}>{data}</Text>
    </div>
);
const UserInfoPanel = ({ userModalOpen, setUserModalOpen }: { userModalOpen: boolean; setUserModalOpen: (open: boolean) => void }) => {
    const { user, authClient } = useAuth();
    const t = useNestedTranslation('user.profile');
    const { i18n } = useTranslation();
    const { displayToast } = useToast();

    if (!user) {
        return null;
    }

    const roles = user.role ?? [];

    return (
        <div data-testid="user-info-panel">
            <UserProfileDrawer
                user={getCWDSUserProfile(user)}
                sections={[
                    {
                        title: t('personalInformation'),
                        description: `${t.global('country')}, ${t.global('businessUnit')}, ${t('email')}, ${t('role')}`,
                        template: (
                            <ListView id="userInfo-panel-details-list-view" data-testid="userInfo-panel-details-list-view" showDivider>
                                <ListViewItem
                                    data-testid="userInfo-panel-details-country"
                                    emphasised
                                    description={(
                                        <Details
                                            title={t.global('country')}
                                            data={translateWithFallback(`lookups.countries.${user.country}`, user.country, i18n)}
                                        />
                                    )}
                                    size="small"
                                />
                                <ListViewItem
                                    data-testid="userInfo-panel-details-unit"
                                    emphasised
                                    description={(<Details title={t.global('businessUnit')} data={user.businessUnit} />)}
                                    size="small"
                                />
                                <ListViewItem
                                    data-testid="userInfo-panel-details-email"
                                    emphasised
                                    description={(<Details title={t('email')} data={user.email} />)}
                                    size="small"
                                />
                                <ListViewItem
                                    data-testid="userInfo-panel-details-company"
                                    emphasised
                                    description={(<Details title={t('company')} data="IKEA" />)}
                                    size="small"
                                />
                                <ListViewItem
                                    data-testid="userInfo-panel-details-role"
                                    emphasised
                                    description={(
                                        <Details
                                            title={t('role')}
                                            data={roles.length <= 1
                                                ? roles[0]
                                                : <div>{roles.map(roleItem => <div key={roleItem}>{roleItem}</div>)}</div>}
                                        />
                                    )}
                                    size="small"
                                />
                            </ListView>
                        ),
                    },
                    {
                        title: t('customize'),
                        description: t('language'),
                        template: (<LanguageSelector />)
                    },
                    {
                        title: t('privacyShareData'),
                        description: `${t('privacyPolicy')}, ${t('dataSharing')}`,
                        template: (
                            <ListView id="userInfo-panel-privacy" data-testid="userInfo-panel-privacy" showDivider={false}>
                                <ListViewItem
                                    data-testid="userInfo-panel-details-privacy"
                                    emphasised
                                    description={(
                                        <Details
                                            title={t('privacyShareDataDesc')}
                                            data={(
                                                <div className={classes['user-info-panel-privacy']} data-testid="user-info-detail-privacy">
                                                    <Trans
                                                        i18nKey={t('privacyShareDataLink')}
                                                        components={[
                                                            <Hyperlink
                                                                key="ey"
                                                                rel="noreferrer"
                                                                url={env.VITE_APP_URLS_user_privacy_policy_url}
                                                                newWindow
                                                                target="_blank"
                                                            />
                                                        ]}
                                                    />
                                                </div>
                                            )}
                                        />
                                    )}
                                    size="small"
                                />
                            </ListView>
                        )
                    }

                ]}
                logoutCallback={() => authClient.logout().catch(() => { displayToast({ title: t('ERROR'), message: t('SIGN_OUT_FAILED') }); })}
                visible={userModalOpen}
                dir="ltr"
                userProfileSectionListMode="LARGE"
                onUserProfileClose={() => setUserModalOpen(false)}
            />
        </div>
    );
};

export default UserInfoPanel;
